import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { DebouncedFunc } from "lodash";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import moment from "moment-timezone";
import { TimelineGL } from "./gl/TimelineGL";
import { Popover, Typography, Grid, Theme, useTheme } from "@mui/material";
import { CrewScheduleGL } from "../CrewSchedule/gl/CrewScheduleGL";
import { WaterfallGL } from "../CrewSchedule/gl/WaterfallGL";

interface IFlightTimelineDatePickerProps {
  anchorID: string;
  timelineGL: TimelineGL | CrewScheduleGL | WaterfallGL;
  display: boolean;
  setDisplay: Dispatch<SetStateAction<boolean>>;
  // Saves the assigned date range to redux.
  debouncedSetScale?: DebouncedFunc<() => any>;
  dataLoadedCheck?: (minTime: number, maxTime: number) => void;
  refreshTimeline: () => any;
}

const inputFontSize = 12;
const inputBoxHeight = 20;

const selectedDayStyles = (theme: Theme): any => ({
  "&.MuiPickersDay-today": {
    borderRadius: "15%",
  },
  "&.Mui-selected": {
    borderRadius: "15%",
  },
});

const timelineDatepickersPopupStyles = (theme: Theme): any => ({
  background: "transparent",
  marginLeft: "10px",
  marginTop: "60px",
  width: 335,
  height: 525,
  overflowY: "hidden",
  boxShadow: "none",
  "& .react-datepicker-wrapper": {
    marginLeft: "20px",
    "& .react-datepicker__input-container": {
      position: "absolute",
      top: 8,
      left: 100,
      "& input": {
        fontFamily: "Heebo, sans-serif",
        fontWeight: 300,
        fontSize: inputFontSize,
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        minHeight: inputBoxHeight,
        position: "relative",
        borderRadius: "2px",
        padding: "2px 8px",
        border: "1px solid " + theme.custom.forms.inputBorder,
        color: theme.custom.forms.inputText,
        backgroundColor: theme.custom.forms.inputBack,
        transition: theme.transitions.create(["color"]),
        boxSizing: "border-box",
        "&::placeholder, &::-webkit-input-placeholder": {
          opacity: 1,
          color: theme.custom.forms.labels,
        },
        "&:hover": {
          borderColor: theme.custom.forms.pristineColor,
        },
        "&:focus": {
          border: "1px solid " + theme.custom.forms.pristineColor,
        },
      },
    },
  },
});

/**
 *
 * @param props - Component used to simplify navigation in the timeline by displaying a calendar that will focus selected date.
 * @returns
 */
export const FlightTimelineDatePicker = (props: IFlightTimelineDatePickerProps): JSX.Element => {
  const { timelineGL, display, setDisplay, debouncedSetScale, dataLoadedCheck, refreshTimeline, anchorID } = props;
  const theme: Theme = useTheme();

  const [timelineMid, setTimelineMid] = useState<Date>(null);

  useEffect(() => {
    if (display && timelineGL?.getTimeRange) {
      const [start, end] = timelineGL.getTimeRange();
      const buffer = (end - start) / 2;
      const midPoint = end - buffer;
      setTimelineMid(new Date(midPoint));
    }
  }, [timelineGL, display]);

  const timelineElement = useMemo(
    () => (timelineGL && anchorID ? document.getElementById(anchorID) : null),
    [anchorID, timelineGL]
  );

  const handleAssignTimelineRange = useCallback(
    (newTime: Date, isStart: boolean) => {
      if (!timelineGL?.getTimeRange && !newTime) {
        return;
      }
      const newTimeEpoch = newTime?.valueOf();
      const [start, end] = timelineGL.getTimeRange();
      const buffer = (end - start) / 2;
      timelineGL.setTimeAxis(newTimeEpoch - buffer, newTimeEpoch + buffer);
      if (dataLoadedCheck) dataLoadedCheck(newTimeEpoch - buffer, newTimeEpoch + buffer);
      refreshTimeline();
      if (debouncedSetScale) debouncedSetScale();
      setDisplay(false);
    },
    [timelineGL, refreshTimeline, dataLoadedCheck, setDisplay, debouncedSetScale]
  );

  return (
    <Popover
      id="timeline-date-picker"
      data-testid="timeline-date-picker"
      open={display && !!timelineElement}
      onClose={() => setDisplay(false)}
      anchorEl={timelineElement}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: timelineDatepickersPopupStyles(theme),
        },
      }}
    >
      <Grid container>
        <Grid container className="start_picker" direction="column" style={{ width: 330 }}>
          <Typography variant="caption">Focus Date</Typography>
          <Grid item>
            <StaticDatePicker
              //@ts-ignore
              defaultValue={timelineMid ? moment.utc(timelineMid.toISOString()) : moment.utc(new Date().toISOString())}
              onChange={(start: Date) => handleAssignTimelineRange(start, true)}
              slotProps={{
                actionBar: {
                  actions: [],
                },
                day: {
                  sx: selectedDayStyles(theme),
                },
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Popover>
  );
};
